import {
  Col,
  Descriptions,
  Input,
  Popconfirm,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Badge,
  Typography,
  message,
  InputNumber,
} from "antd"
import React, { useEffect, useState } from "react"
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileOutlined,
  PlusCircleFilled,
} from "@ant-design/icons"
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore"
import { handleStockChange, deleteMaterial } from "./WarehouseMaterialFunctions"
import { db } from "../../../firebase/config"
import PageHeader from "../../../components/Elements/PageHeader"
import CreateWarehouseMaterial from "./CreateWarehouseMaterial"
import { useAuthContext } from "../../../hooks/useAuthContext"
import UpdateWarehouseMaterial from "./UpdateWarehouseMaterial"
import moment from "moment"
import { RollLocationDisplay } from "../../../components/Rolls/RollLocationDisplay"

const { Link, Text } = Typography

export default function WarehouseMaterials() {
  const [tableLoading, setTableLoading] = useState(true)
  const [materialsData, setMaterialsData] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  //gets the user from auth context
  const user = useAuthContext()

  const [innerTableLoading, setInnerTableLoading] = useState(false)
  const [expandedData, setExpandedData] = useState({})

  //width to add
  const [widthToAdd, setWidthToAdd] = useState(0)

  //loads table data from database
  useEffect(() => {
    setTableLoading(true)
    const matRef = collection(db, "materials")
    const unsubscribe = onSnapshot(matRef, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        const rollsArray = doc.data().rolls ? Object.values(doc.data().rolls) : []
        const widthsArray = rollsArray.map((roll) => roll.rollWidth) // creates array of widths
        const totalRollMeters = rollsArray.reduce((total, roll) => total + roll.remainingMeters, 0) // calculate total roll meters
        const docData = {
          ...doc.data(),
          id: doc.id,
          rollsCount: rollsArray.length,
          widthsArray,
          totalRollMeters,
        }
        results.push(docData)
      })
      setMaterialsData(results)
      console.log(results)
      setTableLoading(false)
    })
    return () => unsubscribe()
  }, [])

  //popover stuff
  const confirm = (record, width) => {
    console.log(record)
    handleAddWidth(record.id, width)
  }
  const cancel = (e) => {
    console.log(e)
  }

  //addds width
  const handleAddWidth = async (docid) => {
    // Reference to your collection and document
    const docRef = doc(db, "materials", docid)
    try {
      await updateDoc(docRef, {
        widths: arrayUnion(widthToAdd),
      })
    } catch (error) {
      console.error("Error adding item:", error)
      message.error("error happened!")
      setWidthToAdd(0)
    } finally {
      message.success("width added!")
      setWidthToAdd(0)
    }
  }

  //removes width
  const handleRemoveWidth = async (docid, value) => {
    const docRef = doc(db, "materials", docid)
    try {
      await updateDoc(docRef, {
        widths: arrayRemove(value),
      })
    } catch (error) {
      console.error("Error adding item:", error)
      message.error("error happened!")
      setWidthToAdd(0)
    } finally {
      message.success("width removed!")
      setWidthToAdd(0)
    }
  }

  //handle width change
  const handleWidthChange = (value) => {
    setWidthToAdd(value)
  }

  //returns popover add
  const returnPopoverAdd = (record) => {
    return (
      <Popconfirm
        title="Enter the number of width to add to the list"
        description={
          <>
            <InputNumber style={{ width: "90%" }} value={widthToAdd} onChange={handleWidthChange} />
          </>
        }
        onConfirm={() => confirm(record)}
        onCancel={cancel}
        okText="Add Width"
        cancelText="Cancel">
        <PlusCircleFilled style={{ color: "red" }} />
      </Popconfirm>
    )
  }

  //default columns for table
  const defaultColumns = [
    {
      title: "Stock",
      dataIndex: "stock",
      align: "center",
      render: (text, value, index) =>
        text === true ? (
          <CheckCircleOutlined
            style={{ color: "#1677ff" }}
            onClick={() => handleStockChange(value)}
          />
        ) : (
          <CloseCircleOutlined
            style={{ color: "#f5222d" }}
            onClick={() => handleStockChange(value)}
          />
        ),
    },
    Table.EXPAND_COLUMN,
    {
      title: "Material",
      dataIndex: "material",
      align: "center",
    },

    {
      title: "Total Meters",
      dataIndex: "totalRollMeters",
      align: "center",
    },
    {
      title: "Eur m2",
      dataIndex: "eurM2",
      align: "center",
    },

    {
      title: "gM2",
      dataIndex: "gM2",
      align: "center",
      sorter: {
        compare: (a, b) => a.gM2 - b.gM2,
      },
    },
    {
      title: "Comment",
      dataIndex: "comment",
      align: "center",
      width: "15%",
    },

    {
      title: "Rolls (pcs)",
      dataIndex: "rollsCount",
      align: "center",
    },
    {
      title: "Roll Widths",
      dataIndex: "widthsArray",
      align: "center",
      key: "widths",
      render: (widthsArray) => (
        <>
          {widthsArray &&
            widthsArray.map((width, i) => (
              <Tag color="blue" key={i}>
                {width}
              </Tag>
            ))}
        </>
      ),
    },
    {
      title: "Avaiable Widths",
      dataIndex: "widths",
      align: "center",
      width: "20%",
      key: "widths",
      render: (widthsArray, record) => (
        <>
          {widthsArray &&
            widthsArray.map((width, i) => (
              <Tag color="blue" key={i}>
                {width} <CloseCircleOutlined onClick={() => handleRemoveWidth(record.id, width)} />
              </Tag>
            ))}
          {returnPopoverAdd(record)}
        </>
      ),
    },

    {
      title: "Action",
      align: "center",
      width: "10%",
      render: (value) => (
        <>
          <Space split>
            <UpdateWarehouseMaterial data={value} />
            <Popconfirm
              title="Are you sure？"
              placement="left"
              onConfirm={(event) => deleteMaterial(event, value.id)}
              okText="Yes"
              cancelText="No">
              <Link style={{ color: "red" }}>delete</Link>
            </Popconfirm>
          </Space>
        </>
      ),
    },
  ]

  //handle search query across table
  const handleSearch = (e) => {
    setSearchQuery(e.target.value)
  }

  //material filter
  const filteredMaterialsData = materialsData.filter((item) =>
    item.material.toLowerCase().includes(searchQuery.toLowerCase())
  )

  //displays extra material info
  //includes table with all the rolls in there
  const expandedRow = (record) => {
    let expandedRoll = []

    const getData = async () => {
      setInnerTableLoading(true)
      try {
        const q = query(collection(db, "rolls"), where("selectedMaterialID", "==", record.id))
        const querySnapshot = await getDocs(q)
        querySnapshot.forEach((doc) => {
          expandedRoll.push({ ...doc.data(), id: doc.id })
        })
        setExpandedData((prev) => ({ ...prev, [record.id]: expandedRoll }))
        setInnerTableLoading(false)
      } catch (err) {
        console.log(err)
        setInnerTableLoading(false)
      }
    }

    // only fetch data if it has not been fetched before
    if (!expandedData[record.id]) {
      getData()
    }

    //default columns for table
    const rollsCols = [
      {
        title: "Location",
        dataIndex: "rollLocation",
        align: "center",
        render: (value) => (
          <>
            <Badge status="processing" text={RollLocationDisplay(value)} />
          </>
        ),
      },
      {
        title: "Width",
        dataIndex: "rollWidth",
        align: "center",
      },
      {
        title: "Meters",
        dataIndex: "remainingMeters",
        align: "center",
      },
      {
        title: "Last Used",
        dataIndex: "updated",
        align: "center",

        render: (value) => (
          <>
            <Text>{value?.seconds ? moment.unix(value.seconds).format("DD/MM/YYYY") : ""}</Text>
          </>
        ),
      },
    ]

    return (
      <>
        <Row>
          <Col span={4}>
            <Descriptions column={1} title="Material Extra Info">
              <Descriptions.Item label="Material">{record.material}</Descriptions.Item>
              <Descriptions.Item label="Slogan">{record.slogan}</Descriptions.Item>
              <Descriptions.Item label="Supplier Material Name">
                {record.supplierMaterialName}
              </Descriptions.Item>
              <Descriptions.Item label="EAN">{record.EAN}</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={20}>
            <Table
              columns={rollsCols}
              size="small"
              bordered
              rowKey={(record) => `${record.id}`}
              dataSource={expandedData[record.id] || []}
              loading={innerTableLoading}
              pagination={{ pageSize: 5 }}
            />
          </Col>
        </Row>
      </>
    )
  }

  const cols =
    user.admin || user.devSpec
      ? defaultColumns // show all columns
      : defaultColumns
          .filter((column) => column.dataIndex !== "eurM2")
          .filter((column) => column.dataIndex !== "gM2") // hide Eur m2 and gM2 columns

  if (tableLoading) {
    return (
      <>
        <Spin />
      </>
    )
  }

  return (
    <div style={{ padding: 15 }}>
      <Row>
        <Col span={24}>
          <PageHeader icon={<FileOutlined />} text=" Materials" />
        </Col>
        <Col span={24}>
          <Space split>
            <CreateWarehouseMaterial />
            <Input.Search
              placeholder="Search anything"
              onChange={handleSearch}
              style={{ width: 200 }}
            />
          </Space>
        </Col>
        <Col span={24}>
          <Table
            style={{ marginTop: "15px" }}
            size="small"
            dataSource={filteredMaterialsData}
            loading={tableLoading}
            columns={cols}
            bordered
            rowKey={(record) => record.id}
            expandable={{
              expandedRowRender: (record) => expandedRow(record),
              onExpand: (expanded, record) => {
                if (expanded && !expandedData[record.id]) {
                  expandedRow(record)
                }
              },
            }}
            pagination={{ defaultPageSize: "50", showSizeChanger: true }}
          />
        </Col>
      </Row>
    </div>
  )
}
